import {EnvTypes, withSystem} from '@packages/systems/wfdl';
import {functionOf, TListOfElement, TText} from '@packages/systems/wfdl-ast';
import {
  TPredicate,
  toBooleanTag,
} from '@packages/systems/plugins-utils/Boolean';
import {
  containsComponentInstance,
  extractTextFromEvalElement,
  type EvalElementType,
} from '@packages/utilities/element-content';

export type {EvalElementType} from '@packages/utilities/element-content';

const SPACE = ' ';
const LINE_BREAK = '\n';
const LINE_BREAK_COLLAPSE_REGEXP = new RegExp(LINE_BREAK + '+', 'g');
// The content contract replaces NBSP (U+00A0) with an ordinary space after
// concatenation, for every representation: a plain string from the renderer,
// evaluated element children, and a stored default node. The per-representation
// extractors disagree (evaluated children normalize; stored nodes do not), so
// this single pass is what makes the forms observably equal.
const NON_BREAKING_SPACE_REGEXP = new RegExp('\u00a0', 'g'); // U+00a0
const ZERO_WIDTH_JOINER_REGEXP = new RegExp('\u200D', 'g'); // U+200D

const toText = (elements: EvalElementType[]): string =>
  Array.isArray(elements)
    ? elements
        .reduce(extractTextFromEvalElement, '')
        .replace(NON_BREAKING_SPACE_REGEXP, SPACE)
        .replace(LINE_BREAK_COLLAPSE_REGEXP, SPACE)
        .replace(ZERO_WIDTH_JOINER_REGEXP, '')
    : '';

const hasContent = (elements: EvalElementType[]): boolean =>
  toText(elements) !== '' ||
  (Array.isArray(elements) && containsComponentInstance(elements));

export const listOfElementsToText: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: functionOf(withSystem({isBindable: true}, TListOfElement), TText),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: toText,
};

export const listOfElementsIsSet: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: TPredicate(TListOfElement),
    exported: 'hidden',
  },
  value: (elements: EvalElementType[]) => toBooleanTag(hasContent(elements)),
};

export const listOfElementsIsNotSet: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: TPredicate(TListOfElement),
    exported: 'hidden',
  },
  value: (elements: EvalElementType[]) => toBooleanTag(!hasContent(elements)),
};
